//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'

//#endregion

import { LocalDictionaryFactory } from '../../api/api.module'
import { Activity, Enums } from '../../../models/common'
import { OpConsumer, OpDataTypeInGroup } from '../../../models/core'

import { CreateEditInitParams } from '../../common/create-edit-popup/create-edit-popup.controller'
import { Details, DetailsScope, DetailsAccordion, DetailsAccordionSection } from '../../common/common.details'
import { DetailsParametersOptions } from '../../common/details-parameters/details-parameters.directive'
import { DetailsPopupOptions, DetailsOptions } from '../../common/details-popup/details-popup.directive'
import { HistorySource } from '../../common/history/history.directive.controller'
import { LoggerService } from '../../common/common.module'
import { ConsumerDetailsNotificationsOptions } from './notifications/consumers-details-notifications.controller'

import { ConfigHelper, ConvertHelper, DataTypeGroupComponent, EventsNames, IUserSession, LocalizationHelper, LogHelper, OperatorLayoutSettings, TemplateHelper, ToastHelper, TypeHelper } from '../../../helpers'
import { ConsumerHelper } from '../consumers.helper'
import { ConsumerDetailsPaymentsOptions } from './payments'

interface ConsumerDetailsScope extends DetailsScope {
    CONSUMER_COMMON_TAB_NAME: string
    CONSUMER_HISTORY_TAB_NAME: string

    CONSUMER_PARAMS_ACCORDION_NAME: string
    CONSUMER_NOTIFICATIONS_ACCORDION_NAME: string
    CONSUMER_PAYMENTS_ACCORDION_NAME: string

    id: number

    consumerHistoryOptions: HistorySource
    detailsParametersOptions: DetailsParametersOptions

    noActiveAccordions: string
    noDefinedContent: string

    deleteConfirmationOptions: {
        title: string

        onConfirmed: () => void
    }

    consumerDetailsNotificationsOptions: ConsumerDetailsNotificationsOptions
    consumerDetailsPaymentsOptions: ConsumerDetailsPaymentsOptions
}

export class ConsumerDetailsDirectiveController extends Details<ConsumerDetailsScope> {

    // #region Members

    private referenceObject: OpConsumer
    private referenceObjectId: number

    // #endregion


    // #region Ctor

    constructor(
        $scope: ConsumerDetailsScope,
        $q: ng.IQService,
        $timeout: ng.ITimeoutService,
        logger: LoggerService,
        localDictionary: LocalDictionaryFactory,
        protected userSession: IUserSession,
        protected referenceType: Enums.ReferenceType,
        private moduleName: Enums.SimaxModuleName
    ) {
        super($scope, $timeout, $q, logger, localDictionary, OperatorLayoutSettings.ContactPopupTabActive, userSession, referenceType)

        this.setConfirmationsOptions()
        this.setEvents()
        this.setOptions()
        this.setTemplateConsts()

        if (this.userSession.getLoggedOperator().hasPermission(Activity.CONSUMER_LIST)) {
            this.configureTabs(this.getTabs())
        }
    }

    // #endregion


    // #region Events

    private onOpen(options: DetailsOptions): void {
        this.$scope.detailsOpenParams = options

        this.referenceObjectId = options.referenceObjectId

        this.setHistoryOptions()

        this.onClear()
        this.showPopup()
        this.onRefresh()
    }

    private onRefresh(toRefresh: OpConsumer = null): void {
        this.$scope.options.isDataLoaded = false

        const deferred: JQueryDeferred<OpConsumer> = $.Deferred<OpConsumer>()

        if (!toRefresh) {
            this.userSession
                .getDataProvider()
                .Consumer
                .getConsumer(this.referenceObjectId)
                .then((result: OpConsumer) => {
                    this.$scope.popupTitle = result.toString()
                    deferred.resolve(result)
                })
                .fail((error: any) => {
                    const errorMsg: string = ConvertHelper.getRequestErrorMessage(error)
                    ToastHelper.showError(errorMsg || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))
                    LogHelper.warn(ConsumerHelper.LOG_PREFIX, `Unable to Load data. Error: ${errorMsg}.`)
                })
                .always(() => {
                    this.$scope.showTabs = true
                })
        } else {
            deferred.resolve(toRefresh)
        }

        deferred
            .promise()
            .then((result: OpConsumer) => {
                if (result && (!this.referenceObject || this.referenceObject.idConsumer == result.idConsumer)) {
                    this.referenceObject = result

                    return $.when(
                        this.setBasicParameters(),
                        this.initializeConsumerDetailsNotificationsOptions(),
                        this.initializeConsumerDetailsPaymentsOptions(),
                    )
                }
            })
            .always(() => {
                this.$scope.options.isDataLoaded = true
            })
    }

    private onClear(): void {
        this.$scope.popupTitle = TypeHelper.emptyString
        this.$scope.showTabs = false

        this.referenceObject = null
        this.$scope.detailsParametersOptions = null
    }

    private onClose(): void {
        this.hidePopup()
    }

    private onConfirmDelete(): void {
        ConsumerHelper
            .deleteConsumer(this.userSession.getDataProvider(), this.referenceObjectId, this.moduleName)
            .then(() => {
                this.$scope.$root.$broadcast(EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__DELETED, this.moduleName))
                this.onClose()
            })
            .fail((error: any) => {
                this.logger.log(error)
            })
    }

    private onDelete(): void {
        this.$scope.$broadcast(
            EventsNames.OPEN_CONFIRMATION_POPUP + this.$scope.detailsPopupId,
            LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__CONFIRM_DELETE, this.moduleName)
        )
    }

    private onEditConsumerParams(): void {
        const createEditInitParams: CreateEditInitParams = ConsumerHelper.getCreateEditOptions(this.referenceObject.idConsumer)
        this.$scope.$root.$broadcast(EventsNames.INITIALIZE_CREATE_EDIT_POPUP, createEditInitParams)
    }

    // #endregion


    // #region Setup

    private initializeConsumerDetailsNotificationsOptions(): void {
        this.$scope.consumerDetailsNotificationsOptions = {
            idConsumer: this.referenceObject.idConsumer,
            consumer: this.referenceObject
        }
    }

    private initializeConsumerDetailsPaymentsOptions(): void {
        this.$scope.consumerDetailsPaymentsOptions = {
            idConsumer: this.referenceObject.idConsumer,
            consumer: this.referenceObject
        }
    }

    private setBasicParameters(): JQueryPromise<void> {
        const def: JQueryDeferred<void> = $.Deferred<void>()

        DataTypeGroupComponent
            .getDataTypeInGroupByModule(
                this.userSession.getDataProvider(),
                Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__PANEL_PARAMS_TO_DISPLAY,
                this.moduleName
            )
            .then((dataTypeInGroup: OpDataTypeInGroup[]) => {
                this.$scope.detailsParametersOptions = {
                    dataTypes: dataTypeInGroup,
                    referenceType: this.referenceType,
                    referenceObject: this.referenceObject,
                    useDynamicValues: true,
                    onDataLoaded: () => {
                        def.resolve()
                    }
                }
            })
            .catch(() => {
                def.reject()
            })

        if (this.isAccordionHidden(this.$scope.CONSUMER_PARAMS_ACCORDION_NAME)) {
            def.resolve()
        }

        return def.promise()
    }

    public getAccordions(): DetailsAccordion[] {
        const accordions: DetailsAccordion[] = []
        let items: DetailsAccordionSection[] = []

        if (!this.userSession.getLoggedOperator().hasPermission(Activity.CONSUMER_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.ConsumerParameters)) {
            items = []

            items.push({
                name: this.$scope.CONSUMER_PARAMS_ACCORDION_NAME,
                showInTab: this.$scope.CONSUMER_COMMON_TAB_NAME,
                showStatus: true,
                title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.BasicParameters),
                visible: true,
                canEdit: this.userSession.getLoggedOperator().hasPermission(Activity.CONSUMER_EDIT)
                    && !this.userSession.getLoggedOperator().hasPermission(Activity.CONSUMER_DETAILS_READONLY_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.ConsumerParameters),
                editMethod: () => this.onEditConsumerParams(),
            })

            accordions.push(new DetailsAccordion(this.$scope, this.$timeout, OperatorLayoutSettings.ConsumerDetailsAccordions, 0, items))
        }

        if (!this.userSession.getLoggedOperator().hasPermission(Activity.CONSUMER_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.ConsumerPayments)) {
            items = []

            items.push({
                name: this.$scope.CONSUMER_PAYMENTS_ACCORDION_NAME,
                showInTab: this.$scope.CONSUMER_COMMON_TAB_NAME,
                showStatus: false,
                title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Payments),
                visible: true,
                canEdit: false
            })

            accordions.push(new DetailsAccordion(this.$scope, this.$timeout, OperatorLayoutSettings.ConsumerDetailsAccordions, 1, items))
        }

        if (!this.userSession.getLoggedOperator().hasPermission(Activity.CONSUMER_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.ConsumerNotifications)
            && this.userSession.getLoggedOperator().hasPermission(Activity.CONSUMER_NOTIFICATIONS_VIEW)) {
            items = []

            items.push({
                name: this.$scope.CONSUMER_NOTIFICATIONS_ACCORDION_NAME,
                showInTab: this.$scope.CONSUMER_COMMON_TAB_NAME,
                showStatus: false,
                title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Notifications),
                visible: true,
                canEdit: false,
            })

            accordions.push(new DetailsAccordion(this.$scope, this.$timeout, OperatorLayoutSettings.ConsumerDetailsAccordions, 2, items))
        }
        return accordions
    }

    public getTabs(): any[] {
        const tabs: any[] = []

        if (!this.userSession.getLoggedOperator().hasPermission(Activity.CONSUMER_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.ConsumerParameters)
            || !this.userSession.getLoggedOperator().hasPermission(Activity.CONSUMER_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.ConsumerPayments)
            || (!this.userSession.getLoggedOperator().hasPermission(Activity.CONSUMER_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.ConsumerNotifications) && this.userSession.getLoggedOperator().hasPermission(Activity.CONSUMER_NOTIFICATIONS_VIEW))
        ) {
            tabs.push({
                template: TemplateHelper.getContextMenuItem(
                    LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Common),
                    ConfigHelper.Icon.HOME,
                    this.userSession
                ),
                name: this.$scope.CONSUMER_COMMON_TAB_NAME
            })
        }

        if (!this.userSession.getLoggedOperator().hasPermission(Activity.CONSUMER_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.ConsumerHistory)) {
            tabs.push({
                template: TemplateHelper.getContextMenuItem(
                    LocalizationHelper.getLocalizedText(LocalizationHelper.Text.History),
                    ConfigHelper.Icon.HISTORY,
                    this.userSession
                ),
                name: this.$scope.CONSUMER_HISTORY_TAB_NAME
            })
        }

        return tabs
    }

    private setHistoryOptions(): void {
        this.$scope.consumerHistoryOptions = {
            dataMethodName: null, // DO USUNIĘCIA
            id: null, // DO USUNIĘCIA
            name: this.$scope.CONSUMER_HISTORY_TAB_NAME,
            referenceType: this.referenceType,
            referenceValue: this.referenceObjectId,
            allowColumnReordering: !this.userSession
                .getLoggedOperator()
                .hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.ConsumerHistory),
        }
    }

    private setConfirmationsOptions(): void {
        this.$scope.deleteConfirmationOptions = {
            title: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETING, this.moduleName),
            onConfirmed: () => this.onConfirmDelete()
        }
    }

    private setEvents(): void {
        this.$scope.$on(
            EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__OPEN_DETAILS_POPUP, this.moduleName) + this.$scope.id,
            (event: ng.IAngularEvent, ...args: any[]) => this.onOpen(args[3])
        )

        this.$scope.$on(
            EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__CLOSE_DETAILS_POPUP, this.moduleName) + this.$scope.id,
            () => this.onClose()
        )

        this.$scope.$on(EventsNames.CLOSE_CREATE_EDIT_POPUP, (event: ng.IAngularEvent, ...args: any[]) => this.onRefresh(args[0]))
    }

    private setOptions(): void {
        this.$scope.options = <DetailsPopupOptions>{
            id: this.$scope.id,
            closeEventName: EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__CLOSE_DETAILS_POPUP, this.moduleName),
            delHasPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.CONSUMER_DELETE) ? Activity[Activity.CONSUMER_DELETE] : null,
            openEventName: EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__OPEN_DETAILS_POPUP, this.moduleName),
            title: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DETAILS, this.moduleName),
            deleteMethod: () => this.onDelete(),
            getAccordionDataMethod: () => this.getAccordionData(),
            getAccordionSelectionMethod: () => this.getAccordionSelection(),
            updateAccordionMethod: (names: any[]) => this.updateAccordions(names)
        }
    }

    private setTemplateConsts(): void {
        this.$scope.CONSUMER_COMMON_TAB_NAME = 'common'
        this.$scope.CONSUMER_HISTORY_TAB_NAME = 'history'
        this.$scope.CONSUMER_PARAMS_ACCORDION_NAME = 'CONSUMER_PARAMS_ACCORDION_NAME'
        this.$scope.CONSUMER_NOTIFICATIONS_ACCORDION_NAME = 'CONSUMER_NOTIFICATIONS_ACCORDION_NAME'
        this.$scope.CONSUMER_PAYMENTS_ACCORDION_NAME = 'CONSUMER_PAYMENTS_ACCORDION_NAME'
        this.$scope.noActiveAccordions = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.NoActiveAccordions)
        this.$scope.noDefinedContent = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.NoDefinedContent)
    }


    // #endregion

    resetData() { this.onClear() }//DO USUNIECIA!!! - zastąpić onClear
    loadData(id: number, objects: any[]) { this.onOpen(null) }//DO USUNIECIA!!! - zastąpić onOpen
    updateData(result: any) { this.onRefresh(result) }//DO USUNIECIA!!! - zastąpić onRefresh
}